import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { financialDocumentsMessages } from 'src/features/financialDocuments/utils/messages';
import { FinancialDocument } from 'src/features/financialDocuments/utils/types';
import { LIST_FINANCIAL_DOCUMENTS_RESOURCE } from 'src/features/financialDocuments/api/useListFinancialDocuments';
import { useIssueFinancialDocument } from 'src/features/financialDocuments/api/useIssueFinancialDocument';
import { FINANCIAL_DOCUMENT_RESOURCE } from 'src/features/financialDocuments/api/useGetFinancialDocument';
import { useOnError } from 'src/utils/errors';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

interface IssueFinancialDocumentFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedFinancialDocument: FinancialDocument;
    children?: React.ReactNode;
}

function IssueFinancialDocumentFormModal<TData>({
    selectedFinancialDocument,
    children,
}: IssueFinancialDocumentFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();

    const onError = useOnError();

    const form = useForm({
        resolver: zodResolver(z.object({})),
    });

    const { mutate: issueFinancialDocument } = useIssueFinancialDocument({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_FINANCIAL_DOCUMENTS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [FINANCIAL_DOCUMENT_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(financialDocumentsMessages.issueFinancialDocumentSuccess),
                description: formatMessage(financialDocumentsMessages.issueFinancialDocumentSuccessDescription),
            });
        },
        onError: (error) => onError(error, form.setError),
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...financialDocumentsMessages.issueFinancialDocument },
                subTitle: {
                    ...financialDocumentsMessages.issueFinancialDocumentSubtitle,
                },
                icon: ICON_MAP.INVOICE,
                confirmationMessage: { ...financialDocumentsMessages.confirmIssueFinancialDocument },
            }}
            callbackButton={{
                variant: 'default',
                buttonTitle: financialDocumentsMessages.issue,
                callbackFn: () => {
                    return (
                        issueFinancialDocument({
                            documentId: selectedFinancialDocument.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default IssueFinancialDocumentFormModal;
